/**
 * Copyright 2017 OpenStack Foundation
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 * http://www.apache.org/licenses/LICENSE-2.0
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

/*
 * *******************************  IMPORTANT  **********************************
 * This utils file should hold only methods used globally across files/components,
 * not methods used for one specific purpose/file
 * ******************************************************************************
 * */

import moment from "moment-timezone";
import { epochToMomentTimeZone } from "openstack-uicore-foundation/lib/utils/methods";
import {
  getAccessToken,
  initLogOut
} from "openstack-uicore-foundation/lib/security/methods";
import Swal from "sweetalert2";
import URI from "urijs";
import * as Sentry from "@sentry/react";
import T from "i18n-react/dist/i18n-react";
import {
  BADGE_QR_MINIMUM_EXPECTED_FIELDS,
  BYTES_IN_MEGABYTE,
  DATETIME_FORMAT,
  ERROR_CODE_401,
  ERROR_CODE_403,
  ERROR_CODE_412,
  ERROR_CODE_500,
  HEX_RADIX,
  INT_BASE,
  MARKETING_SETTING_TYPE_HEX_COLOR,
  MILLISECONDS_TO_SECONDS,
  ONE_MINUTE,
  OR_FILTER
} from "./constants";

const DAY_IN_SECONDS = 86400; // 86400 seconds per day
const ELLIPSIS = 3;

export const truncateText = (string, length) =>
  string?.length > length
    ? `${string.substring(0, length - ELLIPSIS)}...`
    : string;

export const canonicalizeObject = (entity) => {
  Object.entries(entity).forEach(([key, value]) => {
    entity[key] = value == null ? "" : value;
  });
};

export const groupByDate = (array, prop, sortBy) => {
  const groupedUnordered = array.reduce((groups, item) => {
    const val = item[prop];
    groups[val] = groups[val] || [];
    groups[val].push(item);
    return groups;
  }, {});

  const groupedOrdered = {};
  Object.keys(groupedUnordered)
    .sort((a, b) => {
      const compareA = groupedUnordered[a][0][sortBy];
      const compareB = groupedUnordered[b][0][sortBy];
      const ONE = 1;
      const MINUS_ONE = 1;
      if (compareA > compareB) {
        return ONE;
      }
      if (compareA < compareB) {
        return MINUS_ONE;
      }
      return 0;
    })
    .forEach((key) => {
      groupedOrdered[key] = groupedUnordered[key];
    });

  return groupedOrdered;
};

export const scrollToError = (errors) => {
  if (Object.keys(errors).length > 0) {
    const firstError = Object.keys(errors)[0];
    const firstNode = document.getElementById(firstError);
    if (firstNode)
      firstNode.scrollIntoView({ behavior: "smooth", block: "center" });
  }
};

export const hasErrors = (field, errors) => {
  if (field in errors) {
    return errors[field];
  }
  return "";
};

export const shallowEqual = (object1, object2) => {
  const keys1 = Object.keys(object1);
  const keys2 = Object.keys(object2);

  if (keys1.length !== keys2.length) {
    return false;
  }

  // Check if all keys in object1 have the same value in object2
  return keys1.every((key) => object1[key] === object2[key]);
};

export const isEmpty = (obj) => Object.keys(obj).length === 0;

export const isEmptyString = (str) => !str || str.trim().length === 0;

/**
 * Returns true if value is null, undefined, empty/whitespace string,
 * empty array, or empty object.
 */
export const empty = (value) => {
  if (value === null || value === undefined) return true;
  if (typeof value === "string") return value.trim().length === 0;
  if (Array.isArray(value)) return value.length === 0;
  if (typeof value === "object") return Object.keys(value).length === 0;
  return false;
};

export const stripTags = (s) => s?.replace(/(<([^>]+)>)/gi, "") || "";

export const boolToStr = (boolean) => (boolean ? "Yes" : "No");

export const parseAndFormat = (
  dateString,
  inputFormat,
  outputFormat = "MM/DD/YYYY h:mma",
  inputTZ = "UTC",
  outputTZ = "UTC"
) => {
  const parsedDate = moment.tz(dateString, inputFormat, inputTZ).tz(outputTZ);
  return parsedDate.format(outputFormat);
};

export const getAccessTokenSafely = async () => {
  try {
    return await getAccessToken();
  } catch (e) {
    Sentry.captureException(e);
    return initLogOut();
  }
};

export const escapeFilterValue = (value) => {
  value = value.replace(/,/g, "\\,");
  value = value.replace(/;/g, "\\;");
  return value;
};

export const fetchResponseHandler = (response) => {
  if (!response.ok) {
    throw response;
  } else {
    return response.json();
  }
};

export const fetchErrorHandler = (response) => {
  const code = response.status;
  const msg = response.statusText;

  switch (code) {
    case ERROR_CODE_403:
      Swal.fire("ERROR", T.translate("errors.user_not_authz"), "warning");
      break;
    case ERROR_CODE_401:
      Swal.fire("ERROR", T.translate("errors.session_expired"), "error");
      break;
    case ERROR_CODE_412:
      Swal.fire("ERROR", msg, "warning");
      break;
    case ERROR_CODE_500:
      Swal.fire("ERROR", T.translate("errors.server_error"), "error");
      break;
    default:
      break;
  }
};

export const adjustEventDuration = (evt, entity) => {
  const adjustedEntity = { ...entity };
  const { id, type } = evt.target;
  let { value } = evt.target;

  if (type === "datetime") {
    const empty = moment(0);
    if (value.valueOf() === empty.valueOf()) value = null;
    if (value !== null) value = value.valueOf() / MILLISECONDS_TO_SECONDS;
    // if we have both dates, update duration
    if (id === "start_date" && adjustedEntity.end_date) {
      adjustedEntity.duration =
        adjustedEntity.end_date > value ? adjustedEntity.end_date - value : 0;
    } else if (id === "end_date" && adjustedEntity.start_date) {
      adjustedEntity.duration =
        adjustedEntity.start_date < value
          ? value - adjustedEntity.start_date
          : 0;
    } else if (adjustedEntity.duration) {
      // if one of the dates is missing but we have duration, update missing date
      if (id === "start_date") {
        adjustedEntity.end_date = value + adjustedEntity.duration;
      } else {
        adjustedEntity.start_date = value - adjustedEntity.duration;
      }
    }
    adjustedEntity[id] = value;
  } else if (value !== "") {
    // updating duration unless is empty
    const parsedValue = parseInt(value, INT_BASE) * ONE_MINUTE;
    if (!Number.isNaN(parsedValue)) {
      if (adjustedEntity.start_date) {
        // if we have start date, update end date
        adjustedEntity.end_date = adjustedEntity.start_date + parsedValue;
      } else if (adjustedEntity.end_date) {
        // if we only have end date, update start date
        adjustedEntity.start_date = adjustedEntity.end_date - parsedValue;
      }
      adjustedEntity[id] = parsedValue;
    }
  }

  return adjustedEntity;
};

/* eslint no-bitwise: "warn" */
export const uuidv4 = () =>
  // eslint-disable-next-line no-magic-numbers
  ([1e7] + -1e3 + -4e3 + -8e3 + -1e11).replace(/[018]/g, (c) =>
    (
      c ^
      // eslint-disable-next-line no-magic-numbers
      (crypto.getRandomValues(new Uint8Array(1))[0] & (15 >> (c / 4)))
    ).toString(HEX_RADIX)
  );

export const getSummitDays = (summit) => {
  const days = [];
  const summitLocalStartDate = epochToMomentTimeZone(
    summit.start_date,
    summit.time_zone_id
  );
  const summitLocalEndDate = epochToMomentTimeZone(
    summit.end_date,
    summit.time_zone_id
  );
  let currentAuxDay = summitLocalStartDate.clone();

  do {
    const option = {
      value: currentAuxDay.valueOf() / MILLISECONDS_TO_SECONDS,
      label: currentAuxDay.format("MMM Do YYYY")
    };

    days.push(option);

    currentAuxDay = currentAuxDay.clone();
    const ONE_DAY = 1;
    currentAuxDay.add(ONE_DAY, "day");
  } while (!currentAuxDay.isAfter(summitLocalEndDate));

  return days;
};

export const isNumericString = (value) => /^[0-9]*$/.test(value);

export const checkOrFilter = (filters, filter) => {
  // check if filter is OR to return the correct filter
  if (filters?.orAndFilter === OR_FILTER) {
    return filter.map((f) => `or(${f})`);
  }
  return filter;
};

export const validateEmail = (email) =>
  String(email)
    .toLowerCase()
    .match(
      /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|.(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
    );

// Client-side form-validation for the allowed_email_domains field.
// Stricter than summit-api's app/Rules/AllowedEmailDomainsArray.php (see SDS line 59):
//   1. Requires at least one dot-separated label (rejects "@acme", "user@abc"),
//      where the server's `[\w.-]+` accepts dotless suffixes.
//   2. RFC-1035-style labels: each label must start and end with [a-z0-9] and
//      may contain internal hyphens. Rejects underscores and labels with
//      leading/trailing hyphens (e.g. "@acme_corp.com", "@acme-.com",
//      "@-acme.com") that the server's `\w` / `[\w.-]` patterns admit.
// The server remains the authority on save; the UI just blocks obvious noise.
const ALLOWED_DOMAIN_RE =
  /^@[a-z0-9](?:[a-z0-9-]*[a-z0-9])?(?:\.[a-z0-9](?:[a-z0-9-]*[a-z0-9])?)+$/i;
const ALLOWED_TLD_RE = /^\.[a-z0-9]+(?:\.[a-z0-9]+)*$/i;
const ALLOWED_EMAIL_RE =
  /^[^@\s]+@[a-z0-9](?:[a-z0-9-]*[a-z0-9])?(?:\.[a-z0-9](?:[a-z0-9-]*[a-z0-9])?)+$/i;

export const validateAllowedEmailDomainEntry = (entry) => {
  if (typeof entry !== "string") return false;
  const trimmed = entry.trim();
  if (trimmed.length === 0) return false;
  return (
    ALLOWED_DOMAIN_RE.test(trimmed) ||
    ALLOWED_TLD_RE.test(trimmed) ||
    ALLOWED_EMAIL_RE.test(trimmed)
  );
};

export const getAvailableBookingDates = (summit) => {
  const isValidStartDate =
    new Date(summit.begin_allow_booking_date).getTime() > 0;
  const isValidEndDate = new Date(summit.end_allow_booking_date).getTime() > 0;
  const now = moment().tz(summit.time_zone_id).startOf("day");
  const dates = [];

  if (!isValidStartDate || !isValidEndDate) return dates;

  const bookStartDate = epochToMomentTimeZone(
    summit.begin_allow_booking_date,
    summit.time_zone_id
  ).startOf("day");
  const bookEndDate = epochToMomentTimeZone(
    summit.end_allow_booking_date,
    summit.time_zone_id
  ).startOf("day");

  while (bookStartDate <= bookEndDate) {
    if (bookStartDate >= now) {
      const tmp = bookStartDate.clone();
      dates.push({ str: tmp.format("MMM Do YYYY"), epoch: tmp.unix() });
    }
    bookStartDate.add(1, "days");
  }
  return dates;
};

const isEntityWithinDay = (dayValue, entity) => {
  const startOfDay = dayValue;
  const endOfDay = dayValue + DAY_IN_SECONDS;

  return entity.start_datetime >= startOfDay && entity.end_datetime <= endOfDay;
};

export const getDayFromReservation = (entity, availableDates) => {
  const matchingDay = availableDates.find((date) =>
    isEntityWithinDay(date.epoch, entity)
  );
  return matchingDay?.epoch || null;
};

export const wrapFormFile = (file) => {
  const formData = new FormData();
  formData.append("file", file);
  return formData;
};

export const joinCVSChunks = (chunks) => {
  // if we get result try to get first the header
  const header = chunks[0].split("\n")[0];
  // and rebuild all the chunks using reduce
  const csv = chunks.reduce((final, currentCvs) => {
    const lines = currentCvs.split("\n");
    // remove one line, starting at the first position
    // eslint-disable-next-line no-magic-numbers
    lines.splice(0, 1);
    const rawContent = lines.join("\n");
    return final === "" ? rawContent : `${final}${rawContent}`;
  }, "");

  return `${header}\n${csv}`;
};

// Spreadsheets run a text cell starting with one of these as a formula, even
// when quoted; a leading apostrophe keeps it literal (OWASP CSV injection).
const CSV_FORMULA_PREFIX = /^[=+\-@\t\r]/;

// RFC 4180: every field quoted, embedded quotes doubled, CRLF line endings.
// Text is formula-neutralized; numbers pass through unchanged.
export const toCSV = (rows) =>
  rows
    .map((row) =>
      row
        .map((value) => {
          const text = String(value ?? "");
          const safe =
            typeof value === "string" && CSV_FORMULA_PREFIX.test(text)
              ? `'${text}`
              : text;
          return `"${safe.replace(/"/g, "\"\"")}"`;
        })
        .join(",")
    )
    .map((line) => `${line}\r\n`)
    .join("");

export const joinCVSChunksAndNormalizeHeaders = (csvFiles) => {
  function parseCsv(csv) {
    const [headerLine, ...lines] = csv.trim().split("\n");
    const headers = headerLine.split(",");
    const data = lines.map((line) => {
      const values = line.split(",");
      const obj = {};
      headers.forEach((key, i) => {
        obj[key] = values[i] || "";
      });
      return obj;
    });
    return { headers, data };
  }

  const allHeadersSet = new Set();
  const allData = [];

  for (const csv of csvFiles) {
    const { headers, data } = parseCsv(csv);
    headers.forEach((h) => allHeadersSet.add(h));
    allData.push(...data);
  }

  const allHeaders = Array.from(allHeadersSet);

  function alignData(row) {
    return allHeaders.map((h) => (row[h] !== undefined ? row[h] : ""));
  }

  const rows = [
    allHeaders.join(","),
    ...allData.map((row) => alignData(row).join(","))
  ];

  return rows.join("\n");
};

export const htmlToString = (html) =>
  new DOMParser().parseFromString(html, "text/html").documentElement
    .textContent;

export const capitalize = (string) => {
  const ONE_LETTER = 1;
  return string
    ? string.charAt(0).toUpperCase() + string.slice(ONE_LETTER)
    : "";
};

export const titleCase = (str) =>
  str
    .toLowerCase()
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.substring(1))
    .join(" ");

export const parseDateRangeFilter = (
  filterObject,
  filterToParse,
  filterName
) => {
  if (filterToParse && filterToParse.some((e) => e !== null && e !== 0)) {
    if (filterToParse.every((e) => e !== null && e !== 0)) {
      filterObject.push(
        `${filterName}[]${filterToParse[0]}&&${filterToParse[1]}`
      );
    } else {
      filterObject.push(
        `${
          filterToParse[0] !== null && filterToParse[0] !== 0
            ? `${filterName}>=${filterToParse[0]}`
            : ""
        }${
          filterToParse[1] !== null && filterToParse[1] !== 0
            ? `${filterName}<=${filterToParse[1]}`
            : ""
        }`
      );
    }
  }
};

export const handleDDLSortByLabel = (ddlArray) =>
  ddlArray.sort((a, b) => a.label.localeCompare(b.label));

export const range = (start, stop, step) =>
  Array.from({ length: (stop - start) / step + 1 }, (_, i) => start + i * step);

export const isHexColorSetting = (entity) =>
  entity.type === MARKETING_SETTING_TYPE_HEX_COLOR;

export const isValidUrl = (url) => {
  try {
    const parsedUrl = new URL(url);
    return parsedUrl.protocol === "http:" || parsedUrl.protocol === "https:";
  } catch (error) {
    return false;
  }
};

export const moveItem = (array, fromIndex, toIndex) => {
  if (fromIndex === toIndex) return array;

  const newArray = [...array];
  const target = newArray[fromIndex];
  const inc = toIndex < fromIndex ? -1 : 1;

  for (let i = fromIndex; i !== toIndex; i += inc) {
    newArray[i] = newArray[i + inc];
  }

  newArray[toIndex] = target;

  return newArray;
};

export const arrayToString = (array, join = ",") => {
  if (Array.isArray(array)) {
    return array.length > 0 ? array.join(join) : "";
  }

  return array;
};

export const capitalizeFirstLetter = (val) =>
  String(val).charAt(0).toUpperCase() + String(val).slice(1);

export const formatBadgeQR = (code, summit) => {
  const qrCodeArray = code.split(summit.qr_registry_field_delimiter);

  if (
    qrCodeArray.length >= BADGE_QR_MINIMUM_EXPECTED_FIELDS &&
    qrCodeArray[0] === summit.badge_qr_prefix
  ) {
    return {
      badgePrefix: qrCodeArray[0],
      ticketNumber: qrCodeArray[1],
      email: qrCodeArray[2] || "",
      fullName: qrCodeArray[3] || ""
    };
  }

  return null;
};

export const getMediaInputValue = (entity, fieldName = "images") => {
  const mediaFiles = entity?.[fieldName];
  if (!mediaFiles?.length) return [];

  return mediaFiles.map((img) => {
    const fileUrl = img.filename ?? img.file_path ?? img.file_url;
    if (!fileUrl) return { ...img, filename: "" };

    const fileName = new URI(fileUrl).filename();
    const publicURL = new URI(img?.public_url || fileUrl)
      .setQuery("t", Date.now())
      .toString();

    return {
      ...img,
      public_url: publicURL,
      filename: fileName
    };
  });
};
// eslint-disable-next-line no-magic-numbers
export const bytesToMb = (bytes) => (bytes / BYTES_IN_MEGABYTE).toFixed(2);

export const normalizeSelectAllField = (
  items,
  flagName,
  listName,
  allSelected
) => {
  const isAllSelected = allSelected ?? items?.includes("all");
  if (isAllSelected) {
    return {
      [flagName]: true,
      [listName]: []
    };
  }

  if (!items?.length)
    return {
      [flagName]: false,
      [listName]: []
    };
  return {
    [flagName]: false,
    [listName]: items.map((a) => a.id ?? a)
  };
};

export const formatDate = (
  date,
  timeZone = "LOC",
  format = DATETIME_FORMAT
) => {
  if (timeZone === "LOC") {
    return moment(date * MILLISECONDS_TO_SECONDS).format(format);
  }

  return moment(date * MILLISECONDS_TO_SECONDS)
    .tz(timeZone)
    .format(format);
};

/**
 * Epoch (seconds) of the first ("start") or last ("end") second of the
 * calendar day carried by a date-picker value, in the given show timezone.
 *
 * Only the calendar date is used: a picker value lives in the admin's browser
 * zone (or the show zone when editing), and re-zoning it with moment.tz()
 * would shift the instant instead of keeping the date the admin picked.
 */
export const dateToShowTZEpoch = (value, timeZone, boundary = "start") => {
  const day = moment.tz(moment(value).format("YYYY-MM-DD"), timeZone);
  return (boundary === "end" ? day.endOf("day") : day.startOf("day")).unix();
};

export const getFileUploadAllowedExtensions = () => {
  const ext = window.FILE_UPLOAD_ALLOWED_EXTENSIONS;
  if (!ext) return [];
  return (Array.isArray(ext) ? ext : String(ext).split(","))
    .map((s) => String(s).trim())
    .filter(Boolean);
};

export const isImageUrl = (url) =>
  /\.(jpe?g|png|gif|webp|svg|bmp)(\?|$)/i.test(url);

export const getSafePageAfterRemove = (totalCount, perPage, currentPage) => {
  const lastPage = Math.ceil(totalCount / perPage);
  const isOnLastPage = currentPage === lastPage;
  // last page had exactly one item, so removing it shrinks the page count
  const pageCountDecreases = Number.isInteger((totalCount - 1) / perPage);
  return isOnLastPage && pageCountDecreases && currentPage > 1
    ? currentPage - 1
    : currentPage;
};

// Strict positive-integer route-id validator. Route params arrive as strings;
// accept only positive integers so a malformed/tampered id can't be interpolated
// into a filter clause, a URL path, or a download filename.
export const isPositiveIntId = (v) => /^[1-9]\d*$/.test(String(v));

// Flatten HTML-ish text to plain text: tag boundaries become spaces (so adjacent
// tags don't fuse words), entities are decoded via the browser parser, whitespace
// is collapsed and trimmed. Use this instead of the existing htmlToString, which
// is documentElement.textContent and fuses adjacent-tag text.
export const htmlToPlainText = (html) => {
  if (html == null) return "";
  const spaced = String(html).replace(/<[^>]*>/g, " ");
  const decoded = new DOMParser().parseFromString(spaced, "text/html")
    .documentElement.textContent;
  return decoded.replace(/\s+/g, " ").trim();
};

export const formatDuration = (duration) => {
  const d = moment.duration(duration, "seconds");
  const formatted = d.format("mm:ss", { trim: false });
  return formatted !== "00:00" ? formatted : "TBD";
};

// Builds "{count} {noun}" from a singular translation key, appending "s"
export const countLabel = (translationKey, count) =>
  `${count} ${T.translate(translationKey)}${count === 1 ? "" : "s"}`;
